import React, { useState, useEffect } from 'react';
import { TrendingUp, ShieldCheck, Terminal } from 'lucide-react';
import AccountHubDashboard from './AccountHubDashboard';
import McpAccessDashboard from './McpAccessDashboard';
import ApiPortalDashboard from './ApiPortalDashboard';
import ApiKeyPortal from './ApiKeyPortal';
import DashboardNav, { DashboardBrandRow } from './DashboardNav';
import { mcpGet, engineWsUrl, aiActionLogsUrl } from './mcpApi';
import {
  hasValidPortalSession,
  stashRedirectBeforeLogin,
  validatePortalSessionAgainstEngine,
} from './authSession';
import { isGoogleLoginConfigured } from './googleAuthConfig';

/** Top-10 spot pairs for a compact reference strip (not the main focus of this page). */
const TOP_10_REF = [
  'BTCUSDT',
  'ETHUSDT',
  'SOLUSDT',
  'BNBUSDT',
  'XRPUSDT',
  'DOGEUSDT',
  'ADAUSDT',
  'AVAXUSDT',
  'LINKUSDT',
  'DOTUSDT',
];

const SOLANA_MAINNET_RPC = 'https://api.mainnet-beta.solana.com';
const EVM_PUBLIC_RPC = {
  ethereum: 'https://rpc.ankr.com/eth',
  base: 'https://mainnet.base.org',
  arbitrum: 'https://arb1.arbitrum.io/rpc',
  polygon: 'https://polygon-rpc.com',
  bsc: 'https://bsc-dataseed.binance.org',
};
const CHAIN_PRICE_SYMBOL = {
  ethereum: 'ETHUSDT',
  base: 'ETHUSDT',
  arbitrum: 'ETHUSDT',
  polygon: 'MATICUSDT',
  bsc: 'BNBUSDT',
  solana: 'SOLUSDT',
};

function shortTicker(sym) {
  return sym.endsWith('USDT') ? sym.slice(0, -4) : sym;
}

function formatRefPrice(n) {
  if (n == null || !Number.isFinite(n) || n <= 0) {
    return '—';
  }
  if (n >= 1000) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  if (n >= 1) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '$0.00';
  }
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function normalizeChain(chainRaw) {
  const c = String(chainRaw || '').toLowerCase();
  if (!c || c === 'universal') {
    return 'ethereum';
  }
  return c;
}

async function jsonRpcPost(url, method, params) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
  });
  const data = await res.json();
  if (data?.error) {
    throw new Error(data.error.message || 'RPC error');
  }
  return data?.result;
}

function parseHexWeiToNative(hex, decimals = 18) {
  try {
    const bigIntFn = typeof window !== 'undefined' && window.BigInt ? window.BigInt : null;
    if (!bigIntFn) {
      return null;
    }
    const raw = bigIntFn(hex || 0);
    const base = bigIntFn(10) ** bigIntFn(decimals);
    const whole = raw / base;
    const frac = raw % base;
    const fracDigits = frac.toString().padStart(decimals, '0').slice(0, 6);
    const parsed = Number(`${whole.toString()}.${fracDigits}`);
    return Number.isFinite(parsed) ? parsed : Number(whole.toString());
  } catch {
    return null;
  }
}

const App = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isMcpAccessMode = path.startsWith('/mcp-access');
  const isApiPortalMode =
    path.startsWith('/api') || path.startsWith('/build-api-catalog');
  const isAccountHubMode = path.startsWith('/account-hub');
  const isAuthMode =
    path === '/login' ||
    path === '/signup' ||
    path === '/auth' ||
    path === '/auth/login' ||
    path === '/auth/signup';

  const sessionOk =
    typeof window === 'undefined' || isAuthMode || hasValidPortalSession();

  const [refPrices, setRefPrices] = useState(() =>
    Object.fromEntries(TOP_10_REF.map((s) => [s, null]))
  );
  const [logs, setLogs] = useState([]);
  const [aiActionLogs, setAiActionLogs] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [strategies, setStrategies] = useState([]);
  const [walletOverview, setWalletOverview] = useState('No wallet connected');
  const [walletUsdTotal, setWalletUsdTotal] = useState(0);
  const [walletRows, setWalletRows] = useState([]);
  const [selectedWalletId, setSelectedWalletId] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined' || isAuthMode) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (!hasValidPortalSession()) {
        stashRedirectBeforeLogin();
        window.location.replace('/auth/login');
        return;
      }
      const result = await validatePortalSessionAgainstEngine();
      if (cancelled) return;
      if (!result.ok) {
        stashRedirectBeforeLogin();
        window.location.replace('/auth/login');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path, isAuthMode]);

  useEffect(() => {
    if (!sessionOk || isAccountHubMode || isMcpAccessMode || isApiPortalMode) {
      return undefined;
    }

    let cancelled = false;
    const loadBinanceRef = async () => {
      try {
        const symbolsParam = encodeURIComponent(JSON.stringify(TOP_10_REF));
        const res = await fetch(
          `https://api.binance.com/api/v3/ticker/price?symbols=${symbolsParam}`
        );
        if (!res.ok || cancelled) {
          return;
        }
        const arr = await res.json();
        if (!Array.isArray(arr) || cancelled) {
          return;
        }
        setRefPrices((prev) => {
          const next = { ...prev };
          arr.forEach((row) => {
            if (row.symbol && row.price != null) {
              const p = parseFloat(row.price);
              if (Number.isFinite(p)) {
                next[row.symbol] = p;
              }
            }
          });
          return next;
        });
      } catch {
        /* CORS/offline: rely on engine websocket for overlapping symbols */
      }
    };

    loadBinanceRef();
    const poll = setInterval(loadBinanceRef, 30000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [isAccountHubMode, isMcpAccessMode, isApiPortalMode, sessionOk]);

  useEffect(() => {
    if (!sessionOk || isAccountHubMode || isMcpAccessMode || isApiPortalMode) {
      return undefined;
    }

    let cancelled = false;
    const loadWalletOverview = async () => {
      try {
        const payload = await mcpGet('/v1/account-hub/wallets');
        if (cancelled) {
          return;
        }
        const ws = Array.isArray(payload?.wallets) ? payload.wallets : [];
        if (ws.length === 0) {
          setWalletOverview('No wallet connected');
          setWalletRows([]);
          setWalletUsdTotal(0);
          setSelectedWalletId('');
          return;
        }
        const connected = ws.filter((w) => (w?.status || 'connected') === 'connected');
        if (connected.length === 0) {
          setWalletOverview('No active wallet');
          setWalletRows([]);
          setWalletUsdTotal(0);
          setSelectedWalletId('');
          return;
        }
        setWalletOverview(`${connected.length} connected`);

        const priceSymbols = Array.from(
          new Set(connected.map((w) => CHAIN_PRICE_SYMBOL[normalizeChain(w.chain)]).filter(Boolean))
        );
        let prices = {};
        if (priceSymbols.length > 0) {
          try {
            const symbolsParam = encodeURIComponent(JSON.stringify(priceSymbols));
            const res = await fetch(`https://api.binance.com/api/v3/ticker/price?symbols=${symbolsParam}`);
            const arr = await res.json();
            if (Array.isArray(arr)) {
              prices = Object.fromEntries(
                arr
                  .map((row) => [row.symbol, parseFloat(row.price)])
                  .filter(([, v]) => Number.isFinite(v))
              );
            }
          } catch {
            // Keep zero prices if feed fails; card still shows wallet list.
          }
        }

        const rows = await Promise.all(
          connected.map(async (w) => {
            const address = String(w.address || '').trim();
            const chainKey = normalizeChain(w.chain);
            const symbol = CHAIN_PRICE_SYMBOL[chainKey];
            const usdRate = Number(prices[symbol] || 0);
            if (!address || address.startsWith('pending_')) {
              return { id: w.wallet_id, name: w.display_name || w.provider, usd: 0, text: '$0.00' };
            }
            try {
              if (chainKey === 'solana') {
                const lamports = await jsonRpcPost(SOLANA_MAINNET_RPC, 'getBalance', [address]).then(
                  (r) => r?.value
                );
                const native = typeof lamports === 'number' ? lamports / 1e9 : 0;
                const usd = native * usdRate;
                return { id: w.wallet_id, name: w.display_name || w.provider, usd, text: formatUsd(usd) };
              }
              const rpc = EVM_PUBLIC_RPC[chainKey] || EVM_PUBLIC_RPC.ethereum;
              const weiHex = await jsonRpcPost(rpc, 'eth_getBalance', [address, 'latest']);
              const native = parseHexWeiToNative(weiHex, 18) || 0;
              const usd = native * usdRate;
              return { id: w.wallet_id, name: w.display_name || w.provider, usd, text: formatUsd(usd) };
            } catch {
              return { id: w.wallet_id, name: w.display_name || w.provider, usd: 0, text: '$0.00' };
            }
          })
        );
        if (cancelled) {
          return;
        }
        const total = rows.reduce((sum, r) => sum + (Number(r.usd) || 0), 0);
        setWalletRows(rows);
        setWalletUsdTotal(total);
        setSelectedWalletId((prev) =>
          prev && rows.some((r) => r.id === prev) ? prev : rows[0]?.id || ''
        );
      } catch {
        if (!cancelled) {
          setWalletOverview('Wallet status unavailable');
          setWalletRows([]);
          setWalletUsdTotal(0);
          setSelectedWalletId('');
        }
      }
    };

    void loadWalletOverview();
    const timer = setInterval(loadWalletOverview, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isAccountHubMode, isMcpAccessMode, isApiPortalMode, sessionOk]);

  useEffect(() => {
    if (!sessionOk || isAccountHubMode || isMcpAccessMode || isApiPortalMode) {
      return undefined;
    }

    const wsUrl = engineWsUrl();
    if (!wsUrl) {
      return undefined;
    }

    const socket = new WebSocket(wsUrl);
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'price') {
        const sym = data.symbol || 'BTCUSDT';
        const val = typeof data.value === 'number' ? data.value : parseFloat(data.value);
        if (!Number.isFinite(val)) {
          return;
        }
        if (TOP_10_REF.includes(sym)) {
          setRefPrices((prev) => ({ ...prev, [sym]: val }));
        }
      }
      if (data.type === 'trade') {
        setLogs((prev) =>
          [{ id: Date.now() + Math.random(), time: new Date().toLocaleTimeString(), msg: data.msg }, ...prev].sort(
            (a, b) => b.id - a.id
          )
        );
        setActiveCount((prev) => Math.max(0, prev - 1));
        if (data.strategy_id) {
          setStrategies((prev) => prev.filter((s) => s.strategy_id !== data.strategy_id));
        }
      }

      if (data.type === 'execution') {
        setLogs((prev) =>
          [
            {
              id: Date.now() + Math.random(),
              time: data.time || new Date().toLocaleTimeString(),
              msg: data.msg,
            },
            ...prev,
          ].sort((a, b) => b.id - a.id)
        );
      }

      if (data.type === 'deploy') {
        setActiveCount((prev) => prev + 1);
        if (data.strategy) {
          setStrategies((prev) => [...prev, data.strategy]);
        }
      }
      if (data.type === 'init_strategies') {
        setActiveCount(data.count);
        if (data.strategies) {
          setStrategies(data.strategies);
        }
      }
    };
    return () => socket.close();
  }, [isAccountHubMode, isMcpAccessMode, isApiPortalMode, sessionOk]);

  useEffect(() => {
    if (!sessionOk || isAccountHubMode || isMcpAccessMode || isApiPortalMode) {
      return undefined;
    }

    let cancelled = false;

    const fetchAiActionLogs = async () => {
      const logsUrl = aiActionLogsUrl(30);
      if (!logsUrl) {
        return;
      }
      try {
        const res = await fetch(logsUrl);
        if (!res.ok) {
          return;
        }
        const payload = await res.json();
        if (!cancelled && Array.isArray(payload.logs)) {
          setAiActionLogs(payload.logs);
        }
      } catch {
        // Silent fail keeps dashboard usable if ML service is temporarily offline.
      }
    };

    fetchAiActionLogs();
    const timer = setInterval(fetchAiActionLogs, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isAccountHubMode, isMcpAccessMode, isApiPortalMode, sessionOk]);

  if (isMcpAccessMode && !sessionOk) {
    return (
      <div className="min-h-screen bg-[#0a0613] flex flex-col items-center justify-center gap-2 text-slate-400 font-mono text-sm px-6">
        <p className="text-violet-300/90">Redirecting to sign in…</p>
      </div>
    );
  }
  if (isMcpAccessMode) {
    return <McpAccessDashboard />;
  }
  if (isApiPortalMode && !sessionOk) {
    return (
      <div className="min-h-screen bg-[#0a0613] flex flex-col items-center justify-center gap-2 text-slate-400 font-mono text-sm px-6">
        <p className="text-violet-300/90">Redirecting to sign in…</p>
      </div>
    );
  }
  if (isApiPortalMode) {
    return <ApiPortalDashboard />;
  }
  if (isAccountHubMode && !sessionOk) {
    return (
      <div className="min-h-screen bg-[#0a0613] flex flex-col items-center justify-center gap-2 text-slate-400 font-mono text-sm px-6">
        <p className="text-violet-300/90">Redirecting to sign in…</p>
      </div>
    );
  }
  if (isAccountHubMode) {
    return <AccountHubDashboard />;
  }
  if (isAuthMode) {
    return <ApiKeyPortal authOnly googleLoginEnabled={isGoogleLoginConfigured()} />;
  }
  if (!sessionOk && typeof window !== 'undefined') {
    return (
      <div className="min-h-screen bg-[#0a0613] flex flex-col items-center justify-center gap-2 text-slate-400 font-mono text-sm px-6">
        <p className="text-violet-300/90">Redirecting to sign in…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 font-mono">
      <div className="flex justify-between items-center mb-6 border-b border-slate-800 pb-4">
        <DashboardBrandRow />
        <DashboardNav />
      </div>

      <div className="mb-6 rounded-lg border border-slate-800/80 bg-slate-900/40 px-3 py-2 overflow-x-auto">
        <div className="flex items-baseline gap-1 min-w-max">
          <span className="text-[9px] text-slate-500 uppercase tracking-wider shrink-0 mr-2">
            Spot (ref.)
          </span>
          {TOP_10_REF.map((s) => (
            <span
              key={s}
              className="text-[10px] text-slate-500 tabular-nums shrink-0 inline-flex items-baseline gap-1 pr-4 border-r border-slate-800/80 last:border-0"
            >
              <span className="text-slate-600">{shortTicker(s)}</span>
              <span className="text-slate-300">{formatRefPrice(refPrices[s])}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <StatCardCompact
          icon={<TrendingUp size={15} />}
          label="Active strategies"
          value={activeCount}
          subValue="Bots running"
          color="text-fuchsia-400"
        />
        <StatCardCompact
          icon={<ShieldCheck size={15} />}
          label="Wallet (USDT)"
          value={formatUsd(walletUsdTotal)}
          subValue={walletOverview}
          color="text-orange-400"
          extra={
            walletRows.length > 0 ? (
              <div className="mt-1.5 space-y-1">
                <p className="text-[9px] text-slate-500">Total wallets</p>
                <select
                  value={selectedWalletId}
                  onChange={(e) => setSelectedWalletId(e.target.value)}
                  className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-[9px] text-slate-300"
                >
                  {walletRows.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
                <p className="text-[9px] text-slate-500">Selected wallet</p>
                <span className="text-[9px] text-slate-300">
                  {walletRows.find((w) => w.id === selectedWalletId)?.text || '$0.00'}
                </span>
              </div>
            ) : null
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-lg p-4 min-h-[480px] flex flex-col shadow-2xl">
          <h2 className="text-sm font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
            <TrendingUp size={13} /> Trading bots and strategies
          </h2>
          <div className="overflow-y-auto flex-1 space-y-2 pr-2 custom-scrollbar">
            {strategies.length === 0 && (
              <p className="text-slate-600 text-xs italic">No active strategies…</p>
            )}
            {strategies.map((strat, idx) => (
              <div key={idx} className="bg-slate-950 p-3 rounded border border-slate-800">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-purple-400 font-bold text-sm">{strat.strategy_id}</span>
                  <span className="text-[10px] bg-slate-800 px-2 py-0.5 rounded text-slate-300">
                    {strat.asset || 'BTCUSDT'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div>
                    <span className="text-slate-500">Side:</span>
                    <span
                      className={
                        strat.execution?.side === 'BUY'
                          ? ' ml-1 text-green-400 font-bold'
                          : ' ml-1 text-red-400 font-bold'
                      }
                    >
                      {strat.execution?.side}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Qty:</span>
                    <span className="ml-1 text-slate-300">{strat.execution?.quantity}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-lg p-4 min-h-[480px] flex flex-col shadow-2xl">
          <h2 className="text-sm font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
            <Terminal size={13} /> Logs
          </h2>
          <div className="overflow-y-auto flex-1 space-y-2 pr-2 custom-scrollbar">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Trades and execution</p>
            {logs.length === 0 && (
              <p className="text-slate-600 text-xs italic">Waiting for trades…</p>
            )}
            {logs.map((log) => (
              <div
                key={log.id}
                className="text-[10px] p-2 bg-slate-950 border-l-2 border-blue-500 rounded-r"
              >
                <span className="text-slate-500 mr-2">[{log.time}]</span>
                <span className="text-blue-400 font-bold">{log.msg}</span>
              </div>
            ))}

            <p className="text-[10px] uppercase tracking-wide text-slate-500 pt-3">AI actions</p>
            {aiActionLogs.length === 0 && (
              <p className="text-slate-600 text-xs italic">No AI action logs…</p>
            )}
            {aiActionLogs.map((log) => (
              <div
                key={`ai-${log.id}`}
                className="text-[10px] p-2 bg-slate-950 border-l-2 border-emerald-500 rounded-r"
              >
                <span className="text-slate-500 mr-2">[{formatLogTime(log.timestamp)}]</span>
                <span className="text-emerald-400 font-bold mr-1">{log.agent_id || 'unknown'}</span>
                <span className="text-slate-300">{log.action_type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatLogTime = (timestamp) => {
  if (!timestamp) {
    return '--:--:--';
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const StatCardCompact = ({ icon, label, value, subValue, color, extra }) => (
  <div className="bg-slate-900 px-3 py-2.5 rounded-lg border border-slate-800 flex items-center gap-2.5">
    <div className={`p-1.5 bg-slate-950 rounded border border-slate-800 shrink-0 ${color}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[9px] text-slate-500 uppercase font-bold tracking-wide">{label}</p>
      <p className="text-sm sm:text-base font-bold leading-tight">{value}</p>
      {subValue && <p className="text-[9px] text-slate-500 mt-0.5">{subValue}</p>}
      {extra}
    </div>
  </div>
);

export default App;
